/**
 * Pure reducers that fold realtime messages into an in-memory `MissionRecord`.
 * Rules:
 *  - telemetry arrays stay sorted by `t`; same-`t` samples replace (server dedupes on (id, t) too)
 *  - fast path for in-order appends (the overwhelmingly common case)
 *  - telemetry older than `capMs` behind the newest sample is dropped to bound memory
 *  - every change produces new array/object identities for the parts that changed
 */
import type {
  AircraftSourcesStatus,
  Alert,
  Broadcast,
  CommsMessage,
  EpochMs,
  MediaClip,
  Marker,
  MissionBundle,
  MissionEvent,
  MissionRecord,
  RunSheetItem,
  ServerMessage,
  WeatherObservation,
} from '@apos/shared';

export const DEFAULT_TELEMETRY_CAP_MS = 6 * 3600_000;

/** Index of the first element with t > query. */
function upperBound<T extends { t: EpochMs }>(arr: readonly T[], t: EpochMs): number {
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (arr[mid]!.t <= t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Insert keeping order by `t`. When `sameKey` returns true for an existing element with the same
 * `t` (or, when `matchAnyT`, anywhere), that element is replaced. Returns the input array when
 * nothing changed.
 */
export function insertSorted<T extends { t: EpochMs }>(arr: readonly T[], item: T, same?: (a: T, b: T) => boolean): T[] {
  const n = arr.length;
  const last = arr[n - 1];
  if (!last || last.t < item.t) return [...arr, item];
  const idx = upperBound(arr, item.t);
  // look back over equal-t neighbours for a duplicate
  for (let i = idx - 1; i >= 0 && arr[i]!.t === item.t; i--) {
    if (!same || same(arr[i]!, item)) {
      const copy = arr.slice();
      copy[i] = item;
      return copy;
    }
  }
  const copy = arr.slice();
  copy.splice(idx, 0, item);
  return copy;
}

/** Merge a sorted batch into a sorted array (dedupe on equal t). */
export function mergeSorted<T extends { t: EpochMs }>(arr: readonly T[], batch: readonly T[]): T[] {
  if (batch.length === 0) return arr as T[];
  let out = arr as T[];
  for (const item of batch) out = insertSorted(out, item);
  return out;
}

/** Drop elements older than newest − capMs. */
export function capByAge<T extends { t: EpochMs }>(arr: T[], capMs: number): T[] {
  const last = arr[arr.length - 1];
  if (!last || arr[0]!.t >= last.t - capMs) return arr;
  const cut = upperBound(arr, last.t - capMs - 1);
  return cut > 0 ? arr.slice(cut) : arr;
}

function upsertById<T extends { id: string; t: EpochMs }>(arr: readonly T[], item: T): T[] {
  const i = arr.findIndex((x) => x.id === item.id);
  if (i >= 0) {
    const without = arr.slice();
    without.splice(i, 1);
    return insertSorted(without, item, (a, b) => a.id === b.id);
  }
  return insertSorted(arr, item, (a, b) => a.id === b.id);
}

function removeById<T extends { id: string }>(arr: readonly T[], id: string): T[] {
  const i = arr.findIndex((x) => x.id === id);
  if (i < 0) return arr as T[];
  const copy = arr.slice();
  copy.splice(i, 1);
  return copy;
}

const clipT = (c: MediaClip) => ({ ...c, t: c.startT });

export interface ReduceResult {
  record: MissionRecord;
  /** Newest telemetry `t` seen in this batch (for backfill bookmarks). */
  newestTelemetryT: EpochMs | null;
  /** Entities whose bundle config the caller should refetch. */
  bundleChanged: Set<string>;
  newAlerts: Alert[];
  /** Broadcasts seen for the first time in this batch (receivers post a receipt). */
  newBroadcasts: Broadcast[];
  /** Latest per-aircraft public-source status carried in this batch. */
  sources: AircraftSourcesStatus[];
  /** true when anything in the record changed */
  changed: boolean;
}

/** Fold a batch of server messages into the record. */
export function applyMessages(record: MissionRecord, msgs: readonly ServerMessage[], capMs = DEFAULT_TELEMETRY_CAP_MS): ReduceResult {
  let r = record;
  let newest: EpochMs | null = null;
  const bundleChanged = new Set<string>();
  const newAlerts: Alert[] = [];
  const newBroadcasts: Broadcast[] = [];
  const sources: AircraftSourcesStatus[] = [];
  const touchedAc = new Set<string>();
  const touchedCrew = new Set<string>();
  const touchedCam = new Set<string>();
  // Shallow-copy maps lazily
  let aircraftObs = r.aircraftObs;
  let crewObs = r.crewObs;
  let cameraObs = r.cameraObs;
  const bump = (t: EpochMs) => {
    if (newest === null || t > newest) newest = t;
  };

  for (const msg of msgs) {
    switch (msg.type) {
      case 'aircraft_obs': {
        const o = msg.data;
        if (o.missionId && o.missionId !== r.mission.id) break;
        if (aircraftObs === r.aircraftObs) aircraftObs = { ...aircraftObs };
        aircraftObs[o.aircraftId] = insertSorted(aircraftObs[o.aircraftId] ?? [], o);
        touchedAc.add(o.aircraftId);
        bump(o.t);
        break;
      }
      case 'crew_obs': {
        const o = msg.data;
        if (crewObs === r.crewObs) crewObs = { ...crewObs };
        crewObs[o.userId] = insertSorted(crewObs[o.userId] ?? [], o);
        touchedCrew.add(o.userId);
        bump(o.t);
        break;
      }
      case 'camera_obs': {
        const o = msg.data;
        if (cameraObs === r.cameraObs) cameraObs = { ...cameraObs };
        cameraObs[o.cameraId] = insertSorted(cameraObs[o.cameraId] ?? [], o);
        touchedCam.add(o.cameraId);
        bump(o.t);
        break;
      }
      case 'weather':
        r = { ...r, weather: upsertById<WeatherObservation>(r.weather, msg.data) };
        break;
      case 'marker':
        r = { ...r, markers: msg.op === 'delete' ? removeById<Marker>(r.markers, msg.data.id) : upsertById<Marker>(r.markers, msg.data) };
        break;
      case 'alert': {
        const existed = r.alerts.some((a) => a.id === msg.data.id);
        r = { ...r, alerts: upsertById<Alert>(r.alerts, msg.data) };
        if (!existed) newAlerts.push(msg.data);
        break;
      }
      case 'event':
        r = { ...r, events: upsertById<MissionEvent>(r.events, msg.data) };
        break;
      case 'media': {
        if (msg.op === 'delete') {
          r = { ...r, mediaClips: removeById(r.mediaClips, msg.data.id) };
        } else {
          const list = upsertById(r.mediaClips.map(clipT), clipT(msg.data)).map(({ t: _t, ...c }) => c as MediaClip);
          r = { ...r, mediaClips: list };
        }
        break;
      }
      case 'comms':
        r = { ...r, comms: upsertById<CommsMessage>(r.comms, msg.data) };
        break;
      case 'mission':
        r = { ...r, mission: msg.data };
        break;
      case 'bundle_changed':
        bundleChanged.add(msg.entity);
        break;
      case 'runsheet': {
        const list = r.runSheet ?? [];
        const next = msg.op === 'delete' ? list.filter((i) => i.id !== msg.data.id) : [...list.filter((i) => i.id !== msg.data.id), msg.data];
        r = { ...r, runSheet: sortRunSheet(next) };
        break;
      }
      case 'broadcast': {
        const list = r.broadcasts ?? [];
        const existed = list.some((b) => b.id === msg.data.id);
        r = { ...r, broadcasts: upsertById<Broadcast>(list, msg.data) };
        if (!existed) newBroadcasts.push(msg.data);
        break;
      }
      case 'aircraft_sources':
        sources.push(msg.data);
        break;
      default:
        break;
    }
  }

  if (aircraftObs !== record.aircraftObs) {
    for (const id of touchedAc) aircraftObs[id] = capByAge(aircraftObs[id]!, capMs);
    r = { ...r, aircraftObs };
  }
  if (crewObs !== record.crewObs) {
    for (const id of touchedCrew) crewObs[id] = capByAge(crewObs[id]!, capMs);
    r = { ...r, crewObs };
  }
  if (cameraObs !== record.cameraObs) {
    for (const id of touchedCam) cameraObs[id] = capByAge(cameraObs[id]!, capMs);
    r = { ...r, cameraObs };
  }
  return { record: r, newestTelemetryT: newest, bundleChanged, newAlerts, newBroadcasts, sources, changed: r !== record };
}

/** Merge a `telemetry/since` backfill. */
export function applyBackfill(
  record: MissionRecord,
  data: Pick<MissionRecord, 'aircraftObs' | 'crewObs' | 'cameraObs'>,
  capMs = DEFAULT_TELEMETRY_CAP_MS,
): { record: MissionRecord; newestTelemetryT: EpochMs | null } {
  let newest: EpochMs | null = null;
  const merge = <T extends { t: EpochMs }>(target: Record<string, T[]>, src: Record<string, T[]> | undefined) => {
    if (!src) return target;
    let out = target;
    for (const [id, list] of Object.entries(src)) {
      if (!list?.length) continue;
      if (out === target) out = { ...target };
      const sorted = [...list].sort((a, b) => a.t - b.t);
      out[id] = capByAge(mergeSorted(out[id] ?? [], sorted), capMs);
      const lt = sorted[sorted.length - 1]!.t;
      if (newest === null || lt > newest) newest = lt;
    }
    return out;
  };
  const aircraftObs = merge(record.aircraftObs, data.aircraftObs);
  const crewObs = merge(record.crewObs, data.crewObs);
  const cameraObs = merge(record.cameraObs, data.cameraObs);
  if (aircraftObs === record.aircraftObs && crewObs === record.crewObs && cameraObs === record.cameraObs) {
    return { record, newestTelemetryT: null };
  }
  return { record: { ...record, aircraftObs, crewObs, cameraObs }, newestTelemetryT: newest };
}

/** Newest telemetry `t` anywhere in the record. */
export function newestTelemetryT(record: MissionRecord): EpochMs | null {
  let n: EpochMs | null = null;
  for (const map of [record.aircraftObs, record.crewObs, record.cameraObs] as Array<Record<string, Array<{ t: EpochMs }>>>) {
    for (const list of Object.values(map)) {
      const last = list[list.length - 1];
      if (last && (n === null || last.t > n)) n = last.t;
    }
  }
  return n;
}

/** Copy bundle configuration (which may be fresher) into the record. */
export function syncRecordConfig(record: MissionRecord, bundle: MissionBundle): MissionRecord {
  return {
    ...record,
    mission: bundle.mission,
    members: bundle.members,
    aircraft: bundle.aircraft,
    cameras: bundle.cameras,
    locations: bundle.locations,
    airports: bundle.airports,
    runSheet: bundle.runSheet ? sortRunSheet(bundle.runSheet) : record.runSheet,
  };
}

export function sortRunSheet(items: readonly RunSheetItem[]): RunSheetItem[] {
  return [...items].sort((a, b) => a.order - b.order || (a.scheduledT ?? 0) - (b.scheduledT ?? 0));
}

/** An empty record built from a bundle (planned missions / before `record` loads). */
export function emptyRecord(bundle: MissionBundle): MissionRecord {
  return {
    mission: bundle.mission,
    members: bundle.members,
    aircraft: bundle.aircraft,
    cameras: bundle.cameras,
    locations: bundle.locations,
    airports: bundle.airports,
    aircraftObs: {},
    crewObs: {},
    cameraObs: {},
    weather: [],
    markers: [],
    alerts: [],
    events: [],
    mediaClips: [],
    comms: [],
    runSheet: sortRunSheet(bundle.runSheet ?? []),
    broadcasts: [],
  };
}

/** Normalise a server record (defensive sort; server promises sorted but costs nothing to check). */
export function normaliseRecord(rec: MissionRecord): MissionRecord {
  const sortMap = <T extends { t: EpochMs }>(m: Record<string, T[]> | undefined) => {
    const out: Record<string, T[]> = {};
    for (const [k, v] of Object.entries(m ?? {})) {
      let sorted = true;
      for (let i = 1; i < v.length; i++) if (v[i]!.t < v[i - 1]!.t) { sorted = false; break; }
      out[k] = sorted ? v : [...v].sort((a, b) => a.t - b.t);
    }
    return out;
  };
  const byT = <T extends { t: EpochMs }>(a: T[] | undefined) => [...(a ?? [])].sort((x, y) => x.t - y.t);
  return {
    ...rec,
    aircraftObs: sortMap(rec.aircraftObs),
    crewObs: sortMap(rec.crewObs),
    cameraObs: sortMap(rec.cameraObs),
    weather: byT(rec.weather),
    markers: byT(rec.markers),
    alerts: byT(rec.alerts),
    events: byT(rec.events),
    comms: byT(rec.comms),
    broadcasts: byT(rec.broadcasts),
    runSheet: sortRunSheet(rec.runSheet ?? []),
    mediaClips: [...(rec.mediaClips ?? [])].sort((a, b) => a.startT - b.startT),
  };
}
